const he = require('he');

export const decodeMenus = (menus: Menu[], deepDecode: boolean = false): Menu[] => {
	return menus.map((menu: Menu) => decodeMenu(menu, deepDecode));
};

export const decodeMenu = (menu: Menu, deepDecode: boolean = false): Menu => {
	return {
		...menu,
		name: menu.name ? he.decode(menu.name) : undefined,
		description: menu.description ? he.decode(menu.description) : undefined,
		localization: menu.localization ? decodeLocalization(menu.localization) : menu.localization,
		items: menu.items ? menu.items.map((item: MenuItem) => decodeItem(item, deepDecode)) : [],
		sections: deepDecode && menu.sections?.length ? menu.sections.map((section: MenuSection) => decodeSection(section, deepDecode)) : menu.sections
	};
};

export const decodeSection = (section: MenuSection, deepDecode: boolean = false): MenuSection => {
	return {
		...section,
		name: section.name ? he.decode(section.name) : undefined,
		description: section.description ? he.decode(section.description) : undefined,
		localization: section.localization ? decodeLocalization(section.localization) : section.localization,
		items: deepDecode && section.items?.length ? section.items.map((item: MenuItem) => decodeItem(item, deepDecode)) : section.items
	};
};

export const decodeItem = (item: MenuItem, deepDecode: boolean = false): MenuItem => {
	return {
		...item,
		name: item.name ? he.decode(item.name) : undefined,
		description: item.description ? he.decode(item.description) : undefined,
		ingredients: item.ingredients ? he.decode(item.ingredients) : undefined,
		price_unit: item.price_unit ? he.decode(item.price_unit) : undefined,
		price_type: item.price_type ? he.decode(item.price_type) : undefined,
		localization: item.localization ? decodeLocalization(item.localization) : item.localization,
		options: deepDecode && item.options?.length ? item.options.map((option: OptionGroup) => decodeOptionGroup(option, deepDecode)) : item.options
	} as MenuItem;
};

export const decodeOption = (option: Option, deepDecode: boolean = false): Option => {
	return {
		...option,
		name: option.name ? he.decode(option.name) : undefined,
		description: option.description ? he.decode(option.description) : undefined,
		ingredients: option.ingredients ? he.decode(option.ingredients) : undefined,
		localization: option.localization ? decodeLocalization(option.localization) : option.localization,
		options: deepDecode && option.options?.length ? option.options.map((opt: OptionGroup) => decodeOptionGroup(opt, deepDecode)) : option.options,
		sub_group: deepDecode && option.sub_group ? decodeOptionGroup(option.sub_group, deepDecode) : option.sub_group
	} as Option;
};

export const decodeOptionGroup = (optionGroup: OptionGroup, deepDecode: boolean = false): OptionGroup => {
	optionGroup.values = optionGroup.values?.map((option:Option) => decodeOption(option, deepDecode));
	return {
		...optionGroup,
		name: optionGroup.name ? he.decode(optionGroup.name) : undefined,
		description: optionGroup.description ? he.decode(optionGroup.description) : undefined,
		internal_name: optionGroup.internal_name ? he.decode(optionGroup.internal_name) : undefined,
		localization: optionGroup.localization ? decodeLocalization(optionGroup.localization) : optionGroup.localization
	} as OptionGroup;
};

export const decodeLocalization = (localization: Localization): Localization => {
	const decoded: Localization = {} as Localization;
	for (const locale in localization) {
		decoded[locale] = {
			...localization[locale],
			name: localization[locale].name ? he.decode(localization[locale].name) : localization[locale].name,
			description: localization[locale].description ? he.decode(localization[locale].description) : localization[locale].description,
			content: localization[locale].content ? he.decode(localization[locale].content) : localization[locale].content
		};
	}
	return decoded;
};

export const decodeSuiteOwner = (suiteOwner: SuiteOwner): EditableSuiteOwner => {
	const [firstName, ...otherNames] = suiteOwner.name.split(' ');
	return {
		...suiteOwner,
		firstName: he.decode(firstName),
		lastName: he.decode(otherNames.join(' ')),
		company_name: suiteOwner.company_name ? he.decode(suiteOwner.company_name) : undefined,
		title: suiteOwner.title ? he.decode(suiteOwner.title) : undefined
	};
};

export const decodeSuiteOwners = (suiteOwners: SuiteOwner[]): SuiteOwner[] => {
	return suiteOwners.map((suiteOwner: SuiteOwner) => {
		return {
			...suiteOwner,
			name: he.decode(suiteOwner.name),
			company_name: suiteOwner.company_name ? he.decode(suiteOwner.company_name) : undefined,
			title: suiteOwner.title ? he.decode(suiteOwner.title) : undefined
		};
	});
};

export const decodeEventSuite = (eventSuite: EventSuite): EventSuite => {
	const decodedMenus = eventSuite.menus ? eventSuite.menus.map((menu: EventSuiteMenu) => decodeNameOfEntity(menu)) : null;
	const decodedEquipment = eventSuite.equipment ? eventSuite.equipment.map((equipment: EventSuiteEquipment) => decodeNameOfEntity(equipment)) : null;
	const decodedOrders = eventSuite.orders ? eventSuite.orders.map((order: EventSuiteOrder) => decodeOrder(order)) : null;
	return {
		...eventSuite,
		suite_owner: eventSuite.suite_owner ? {
			...eventSuite.suite_owner,
			name: he.decode(eventSuite.suite_owner.name)
		} : null,
		suite: decodeNameOfEntity(eventSuite.suite) as Suite,
		menus: decodedMenus as EventSuiteMenu[],
		equipment: decodedEquipment as EventSuiteEquipment[],
		orders: decodedOrders as EventSuiteOrder[],
		dietary_restrictions: eventSuite.dietary_restrictions ? he.decode(eventSuite.dietary_restrictions) : null,
		special_requirements: eventSuite.special_requirements ? he.decode(eventSuite.special_requirements) : null,
		pre_order_additional_notes: eventSuite.pre_order_additional_notes ? he.decode(eventSuite.pre_order_additional_notes) : null
	};
};

export const decodeEvent = (event: SuitesEvent, decodeSuites: boolean = true): SuitesEvent => {
	let decodedSuites: EventSuite[] = [];
	if (decodeSuites) {
		decodedSuites = event.event_suites.map((eventSuite: EventSuite) => decodeEventSuite(eventSuite));
	}

	return {
		...event,
		title: he.decode(event.title),
		event_suites: decodeSuites ? decodedSuites : [...event.event_suites],
		event_type: event.event_type ? {
			...event.event_type,
			name: he.decode(event.event_type.name)
		} : null
	};
};

export const decodeDeliveryTimes = (deliveryTimes: DeliveryTimes): DeliveryTimes => {
	const decodedDeliveryTimes: DeliveryTimes = {};
	for (const [key, value] of Object.entries(deliveryTimes)) {
		decodedDeliveryTimes[key] = value.map((eventSuite: EventSuite) => decodeEventSuite(eventSuite));
	}
	return decodedDeliveryTimes;
};

const hasGroupedItems = (order: EventSuiteOrder | Order): order is EventSuiteOrder => {
	return 'groupedItems' in order.data;
};

const hasMenuGroupedItems = (items: any[]): items is OrderMenuGroup[] => {
	return items.length > 0 && 'sections' in items[0];
};

export const decodeOrder = <T extends EventSuiteOrder | Order>(order: T): T => {
	// Grouped items (grouped by sections)
	if(order.data) {
		if(hasGroupedItems(order) && order.data.groupedItems.length) {
			return {
				...order,
				data: {
					...order.data,
					groupedItems: decodeGroupedOrderItems(order.data.groupedItems)
				}
			};
		}

		// Items directly in the data (usual order items)
		else if (order.data.items && order.data.items.length) {
			return {
				...order,
				data: {
					...order.data,
					items: hasMenuGroupedItems(order.data.items)
						? decodeMenuGroupedOrderItems(order.data.items) as unknown as OrderItem[]
						: decodeItems(order.data.items),
					checkout: {
						...order.data.checkout,
						...(order.data.checkout?.delivery && {
							delivery: {
								...order.data.checkout.delivery,
								address: decodeString(order.data.checkout.delivery?.address || '')
							}
						}),
						...(order.data.checkout?.cateringEvent && {
							cateringEvent: {
								...order.data.checkout.cateringEvent,
								name: decodeString(order.data.checkout.cateringEvent.name || ''),
								address: decodeString(order.data.checkout.cateringEvent.address || ''),
								extra: decodeString(order.data.checkout.cateringEvent.extra || '')
							}
						}),
						questions: order.data.checkout.questions ? decodeAnsweredCustomQuestions(order.data.checkout.questions) : undefined,
						pickup: {
							...order.data.checkout.pickup,
							notes: decodeString(order.data.checkout?.pickup?.notes || '')
						}
					}
				}
			};
		}
	}

	return order;
};

export const decodeDashboardOrder = (order: DashboardOrder): DashboardOrder => {
	return {
		...order,
		name: he.decode(order.name),
		cateringEventName: he.decode(order.cateringEventName || '')
	};
};

export const decodeDashboardOrders = (orders: DashboardOrder[]): DashboardOrder[] => {
	return orders.map((order: DashboardOrder) => {
		return decodeDashboardOrder(order);
	});
};

export const decodeGroupedOrderItems = (groupedItems: OrderItemGroup[]): OrderItemGroup[] => {
	return groupedItems.map((orderGroupedDataItem: OrderItemGroup) => {
		if (orderGroupedDataItem.items?.length) {
			return {
				...orderGroupedDataItem,
				name: he.decode(orderGroupedDataItem.name),
				items: decodeItems(orderGroupedDataItem.items)
			};
		}
		return orderGroupedDataItem;
	});
};

export const decodeMenuGroupedOrderItems = (menuGroups: OrderMenuGroup[]): OrderMenuGroup[] => {
	return menuGroups.map((menu: OrderMenuGroup) => ({
		...menu,
		name: he.decode(menu.name),
		sections: menu.sections?.length ? decodeGroupedOrderItems(menu.sections) : menu.sections
	}));
};

export const decodeItems = (items: OrderItem[]): OrderItem[] => {
	const decodedItems = items.map((item: OrderItem) => {
		let decodedOptions: OrderOptionGroup[] = [];
		// Option groups
		if(item.orderOptions?.length) {
			decodedOptions = item.orderOptions.map((orderOption: any) => {
				let decodedValues: OrderOption[] = [];

				// Option group options
				if(orderOption.values?.length) {
					decodedValues = orderOption.values.map((orderOptionValue: any) => {

						let decodedSubOptions: any[] = [];
						// Option sub option groups
						if(orderOptionValue.options?.length) {
							decodedSubOptions = orderOptionValue.options.map((subOption: any) => {
								let decodedSubOptionValues: OrderOptionGroup[] = [];
								// Option sub option group options
								if(subOption.values?.length) {
									decodedSubOptionValues = subOption.values.map((subOptionValue: any) => {
										return {
											...subOptionValue,
											name: he.decode(subOptionValue.name)
										};
									});
								}
								return {
									...subOption,
									name: he.decode(subOption.name),
									...(subOption.values && { values: decodedSubOptionValues })
								};
							});
						}
						return {
							...orderOptionValue,
							name: he.decode(orderOptionValue.name),
							...(orderOptionValue.options && { options: decodedSubOptions })
						};
					});
				}
				return {
					...orderOption,
					name: he.decode(orderOption.name),
					...(orderOption.values && { values: decodedValues })
				};
			});
		}
		return {
			...item,
			name: he.decode(item.name),
			...(item.description && { description: he.decode(item.description) }),
			...(item.localization && { localization: decodeLocalization(item.localization) }),
			...(item.orderOptions && { orderOptions: decodedOptions }),
			parent: item.parent ? {
				...item.parent,
				name: he.decode(item.parent.name)
			} : undefined
		};
	});

	return decodedItems;
};

export const decodeNameOfEntity = (entity: { name: string }): { name: string } => {
	return {
		...entity,
		name: he.decode(entity.name)
	};
};

export const decodeEvents = (events: SuitesEvent[]): SuitesEvent[] => {
	return events.map((event: SuitesEvent) => decodeEvent(event, false));
};

export const decodeMenuGroup = (menuGroup: MenuGroup): MenuGroup => {
	return {
		...menuGroup,
		name: menuGroup.name ? he.decode(menuGroup.name) : '',
		description: menuGroup.description ? he.decode(menuGroup.description) : ''
	};
};

export const decodeAnsweredCustomQuestions = (questions: AnsweredCustomQuestion[] | CheckoutCustomQuestion[]): AnsweredCustomQuestion[] | CheckoutCustomQuestion[] => {
	return questions.map((question: AnsweredCustomQuestion) => {
		const decodedQuestion: AnsweredCustomQuestion = {
			...question,
			question: he.decode(he.decode(question.question)),
			answer: question.answer ? he.decode(he.decode(question.answer)) : question.answer
		};

		// Decode localization if it exists
		if (question.localization) {
			decodedQuestion.localization = {};
			for (const locale in question.localization) {
				decodedQuestion.localization[locale] = {
					question: question.localization[locale].question ? he.decode(question.localization[locale].question) : undefined,
					answer: question.localization[locale].answer ? he.decode(question.localization[locale].answer) : undefined
				};
			}
		}

		return decodedQuestion;
	});
};

export const decodeCustomQuestion = (question: CustomQuestion): CustomQuestion => {
	const decodedQuestion: CustomQuestion = {
		...question,
		question: he.decode(he.decode(question.question))
	};

	// Decode options if they exist (for select_one type)
	// NOTE: select_multiple isn't implemented yet, adding for future proofing
	if (question.question_type === 'select_one' || question.question_type === 'select_multiple') {
		(decodedQuestion as QuestionWithOptions).options = (question as QuestionWithOptions).options.map((option: string) => he.decode(he.decode(option)));
	}

	// Decode localization if it exists
	if (question.localization) {
		decodedQuestion.localization = {};
		for (const locale in question.localization) {
			decodedQuestion.localization[locale] = {
				question: question.localization[locale].question ? he.decode(he.decode(question.localization[locale].question)) : ''
			};

			// Decode localized options if they exist
			if (question.localization[locale].options) {
				decodedQuestion.localization[locale].options = question.localization[locale].options.map((option: string) => he.decode(he.decode(option)));
			}
		}
	}

	return decodedQuestion;
};

export const decodeTag = (tag: MenuItemTag): MenuItemTag => {
	const decodedTag: MenuItemTag = {
		...tag,
		name: tag.name ? he.decode(tag.name) : tag.name
	};

	if (tag.localization) {
		decodedTag.localization = {} as Localization;

		for (const locale in tag.localization) {
			decodedTag.localization[locale] = {
				...tag.localization[locale],
				name: tag.localization[locale].name ? he.decode(tag.localization[locale].name) : tag.localization[locale].name
			};
		}
	}

	return decodedTag;
};

export const decodeTags = (tags: MenuItemTag[]): MenuItemTag[] => {
	return tags.map((tag: MenuItemTag) => decodeTag(tag));
};

export const decodeString = (string: string): string => {
	return he.decode(string);
};

export const decodeInquiryForm = (form: InquiryForm): InquiryForm => {
	const decoded: InquiryForm = {
		...form,
		title: he.decode(form.title),
		description: he.decode(form.description)
	};

	if (decoded.localization) {
		for (const locale in decoded.localization) {
			const localeData = decoded.localization[locale];
			if (localeData.title) {
				localeData.title = he.decode(localeData.title);
			}
			if (localeData.description) {
				localeData.description = he.decode(localeData.description);
			}
		}
	}

	return decoded;
};

export const decodeInquiryFormRequest = (request: InquiryFormRequest): InquiryFormRequest => {
	return {
		...request,
		name: he.decode(request.name),
		additional_questions: request.additional_questions?.length
			? decodeAnsweredCustomQuestions(request.additional_questions) as AnsweredCustomQuestion[]
			: request.additional_questions,
		orders: request.orders?.map(order => ({
			...order,
			event_name: order.event_name ? he.decode(order.event_name) : order.event_name
		}))
	};
};

export const decodeRallyPrompt = (prompt: RallyIntelligencePromptResponse): RallyIntelligencePromptResponse => {
	return {
		...prompt,
		name: prompt.name ? he.decode(prompt.name) : prompt.name,
		description: prompt.description ? he.decode(prompt.description) : prompt.description,
		content: prompt.content ? he.decode(prompt.content) : prompt.content,
		localization: prompt.localization ? decodeLocalization(prompt.localization as Localization) as { [key: string]: LocalizationInfo } : prompt.localization
	};
};

export const decodeRallyPrompts = (prompts: RallyIntelligencePromptResponse[]): RallyIntelligencePromptResponse[] => {
	return prompts.map((prompt: RallyIntelligencePromptResponse) => decodeRallyPrompt(prompt));
};

export const decodeBulkMenuRows = (rows: BulkMenuItemRow[]): BulkMenuItemRow[] => {
	return rows.map((row: BulkMenuItemRow) => ({
		...row,
		name: he.decode(row.name),
		menu_name: he.decode(row.menu_name),
		section_name: row.section_name ? he.decode(row.section_name) : row.section_name,
		location_name: he.decode(row.location_name),
		tags: decodeTags(row.tags ?? []),
		diets: row.diets ?? [],
		allergens: row.allergens ?? []
	}));
};

export const decodeBulkLocationRows = (rows: BulkLocationRow[]): BulkLocationRow[] => {
	return rows.map((row: BulkLocationRow) => ({
		...row,
		location_name: he.decode(row.location_name),
		group_name: row.group_name ? he.decode(row.group_name) : row.group_name,
		tags: decodeTags(row.tags ?? [])
	}));
};

export const decodeBulkJobMenus = (menus: BulkJobMenu[]): BulkJobMenu[] => {
	return menus.map((menu: BulkJobMenu) => ({ ...menu, name: he.decode(menu.name) }));
};